import { useCallback, useEffect, useState } from 'react';
import type { View } from 'react-native';

import type { UseElementVisibility } from './useElementVisibility.types';

/**
 * Observes whether a referenced view intersects the web viewport.
 *
 * NOTE: Returns `undefined` until visibility is known, while observation is disabled,
 *       or when the Intersection Observer API is unavailable.
 *
 * NOTE: The observer is disconnected when the component unmounts or its configuration changes.
 */
export const useElementVisibility: UseElementVisibility = (elementRef, options = {}) => {
  const { isEnabled = true, rootMargin = 0, threshold = 0 } = options;

  // ─── State ─────────────────────────────────────────────────────────────────

  const [visibility, setVisibility] = useState<Visibility | undefined>();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const rootMarginPx = `${Number.isFinite(rootMargin) ? rootMargin : 0}px`;

  const isVisible = isEnabled && visibility?.element === elementRef.current ? visibility.isVisible : undefined; // prettier-ignore

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const element = elementRef.current;

      if (!element) return;

      const isVisible = entries.some((entry) => entry.isIntersecting);

      setVisibility({ element, isVisible });
    },
    [elementRef],
  );

  const toggleObserver = useCallback(() => {
    const element = elementRef.current;

    if (!isEnabled || !element || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: rootMarginPx,
      threshold,
    });

    observer.observe(element as unknown as Element);

    return () => {
      observer.disconnect();
    };
  }, [elementRef, handleIntersection, isEnabled, rootMarginPx, threshold]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(toggleObserver, [toggleObserver]);

  // ───────────────────────────────────────────────────────────────────────────

  return isVisible;
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Visibility = {
  element: View;
  isVisible: boolean;
};
